import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { HighlightNavigator } from '~/features/document-viewer/HighlightNavigator';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { BaseDocumentViewerProps } from '../types/viewer';
import SearchBar from './SearchBar';

type ZoomScale = 'page-width' | number | undefined;

interface Props extends Pick<
  BaseDocumentViewerProps,
  | 'activeHighlightId'
  | 'highlights'
  | 'navigatorLabel'
  | 'onClear'
  | 'onHighlightClick'
> {
  searchListenOn: React.RefObject<HTMLElement>;
  zoom: {
    scale: ZoomScale;
    onChange: (scale: ZoomScale) => void;
  };
}

export const Toolbar = ({
  activeHighlightId,
  highlights = [],
  navigatorLabel,
  searchListenOn,
  zoom,
  onClear,
  onHighlightClick,
}: Props) => {
  const [isVisible, toggleSearchBar] = useToggle(false);
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const enableNavigator = activeHighlightId && highlights.length > 1;
  const zoomActions = useMemo(() => {
    const { scale, onChange } = zoom;

    const isFitPageWidth = scale === 'page-width';
    const zoomScale = Number(scale) || 1;
    const zoomInScale = Math.min(zoomScale + 0.25, 10);
    const zoomOutScale = Math.max(zoomScale - 0.25, 0.25);

    return [
      {
        icon: 'dash' as const,
        mode: 'icon' as const,
        text: intl.formatMessage({
          id: 'Toolbar.zoomOut',
          defaultMessage: 'Zoom out',
          description: 'Zoom out button text',
        }),
        tooltip: intl.formatMessage(
          {
            id: 'Toolbar.zoomOutTooltip',
            defaultMessage: 'Zoom out ({zoomLevel})',
            description:
              'Tooltip for zoom out button with percentage level, zoomLevel is a percentage value. Example: zoomLevel = "125%"',
          },
          {
            zoomLevel: formatNumber(zoomOutScale, {
              decimal: 0,
              style: 'percent',
            }),
          },
        ),
        onClick: () => onChange(zoomOutScale),
      },
      {
        icon: 'plus' as const,
        mode: 'icon' as const,
        text: intl.formatMessage({
          id: 'Toolbar.zoomIn',
          defaultMessage: 'Zoom in',
          description: 'Zoom in button text',
        }),
        tooltip: intl.formatMessage(
          {
            id: 'Toolbar.zoomInTooltip',
            defaultMessage: 'Zoom in ({zoomLevel})',
            description:
              'Tooltip for zoom in button with percentage level, zoomLevel is a percentage value. Example: zoomLevel = "125%"',
          },
          {
            zoomLevel: formatNumber(zoomInScale, {
              decimal: 0,
              style: 'percent',
            }),
          },
        ),
        onClick: () => onChange(zoomInScale),
      },
      {
        icon: 'maximize' as const,
        mode: 'icon' as const,
        status: scale === 'page-width' ? ('active' as const) : undefined,
        text: intl.formatMessage({
          id: 'Toolbar.fitPageWidth',
          defaultMessage: 'Fit page width',
          description: 'Fit page width button text',
        }),
        tooltip: isFitPageWidth
          ? intl.formatMessage({
              id: 'Toolbar.resetToOriginalSize',
              defaultMessage: 'Reset to original size',
              description: 'Tooltip for resetting zoom to original size',
            })
          : intl.formatMessage({
              id: 'Toolbar.fitPageWidthTooltip',
              defaultMessage: 'Fit page width',
              description: 'Tooltip for fitting page to width',
            }),
        onClick: () => onChange(isFitPageWidth ? 1 : 'page-width'),
      },
    ];
  }, [zoom]);

  return (
    <Layout preset="icons" px={4}>
      <Layout align="center" spacing={2}>
        <IconButton
          icon="search"
          status={isVisible ? 'active' : undefined}
          tooltip={intl.formatMessage({
            id: 'Toolbar.search',
            defaultMessage: 'Search',
            description: 'Search button tooltip',
          })}
          onClick={toggleSearchBar}
        />

        <SearchBar
          listenOn={searchListenOn}
          toggleVisiblity={toggleSearchBar}
          isVisible={isVisible}
        />
      </Layout>
      <Actions actions={zoomActions} />

      {enableNavigator && (
        <HighlightNavigator
          activeHighlightId={activeHighlightId}
          highlights={highlights}
          navigatorLabel={navigatorLabel}
          onClear={onClear}
          onHighlightClick={onHighlightClick}
        />
      )}
    </Layout>
  );
};
